// Evaluations module constants: colours, routes, status strings, table names.
// Pure data — no React, no network. Every other file in the module reads these
// instead of repeating string literals.

export const EVAL_COLORS = {
  teal: "#1ebec8",
  green: "#86c65a",
  orange: "#f79153",
  red: "#fa4747",
  dark: "#36454f",
  purple: "#6c5ce7",
  muted: "#7f8c8d",
};

/** Score chip colour per `scoreTone()` result (spec §7.2). */
export const TONE_COLORS = {
  pass: EVAL_COLORS.green,
  warn: EVAL_COLORS.orange,
  fail: EVAL_COLORS.red,
  none: EVAL_COLORS.muted,
};

export const EVAL_BASE_PATH = "/evaluations";
export const SIGNOFF_BASE_PATH = "/evaluation-signoff";

/** Route table (spec §8). Pattern strings for <Route>, builder functions for links. */
export const EVAL_ROUTES = {
  dashboard: EVAL_BASE_PATH,
  records: `${EVAL_BASE_PATH}/records`,
  recordsForUser: `${EVAL_BASE_PATH}/records/:userId`,
  detail: `${EVAL_BASE_PATH}/detail/:evaluationId`,
  perform: `${EVAL_BASE_PATH}/perform`,
  performResume: `${EVAL_BASE_PATH}/perform/:evaluationId`,
  builder: `${EVAL_BASE_PATH}/builder`,
  builderTemplate: `${EVAL_BASE_PATH}/builder/:templateId`,
  evaluators: `${EVAL_BASE_PATH}/evaluators`,
  mine: `${EVAL_BASE_PATH}/mine`,
  signoff: `${SIGNOFF_BASE_PATH}/:token`,
};

export const evalPath = {
  dashboard: () => EVAL_ROUTES.dashboard,
  records: () => EVAL_ROUTES.records,
  recordsForUser: (userId) => `${EVAL_ROUTES.records}/${userId}`,
  detail: (evaluationId) => `${EVAL_BASE_PATH}/detail/${evaluationId}`,
  perform: () => EVAL_ROUTES.perform,
  performResume: (evaluationId) => `${EVAL_ROUTES.perform}/${evaluationId}`,
  builder: () => EVAL_ROUTES.builder,
  builderTemplate: (templateId) => `${EVAL_ROUTES.builder}/${templateId}`,
  evaluators: () => EVAL_ROUTES.evaluators,
  mine: () => EVAL_ROUTES.mine,
  signoff: (token) => `${SIGNOFF_BASE_PATH}/${token}`,
};

/** Tabs in display order; `key` matches the `useEvaluationAccess` flag that shows it. */
export const EVAL_TABS = [
  { key: "dashboard", label: "Dashboard", path: EVAL_ROUTES.dashboard, flag: "canEvaluate" },
  { key: "records", label: "Records", path: EVAL_ROUTES.records, flag: "canEvaluate" },
  { key: "perform", label: "Perform", path: EVAL_ROUTES.perform, flag: "canEvaluate" },
  { key: "builder", label: "Builder", path: EVAL_ROUTES.builder, flag: "canBuild" },
  { key: "evaluators", label: "Evaluators", path: EVAL_ROUTES.evaluators, flag: "canManage" },
  { key: "mine", label: "My Evaluations", path: EVAL_ROUTES.mine, flag: "hasOwn" },
];

export const EVALUATION_STATUS = { DRAFT: "Draft", COMPLETED: "Completed" };
export const TEMPLATE_STATUS = { DRAFT: "Draft", PUBLISHED: "Published", ARCHIVED: "Archived" };
export const SCOPE_TYPES = { DISTRICT: "District", SITE: "Site", AREA: "Area", REPORTS: "Reports" };
export const SCOPE_TYPE_LABELS = {
  District: "Whole district",
  Site: "Specific sites",
  Area: "Areas",
  Reports: "Their direct reports",
};
export const REMEDIATION_STATUS = { ASSIGNED: "assigned", FAILED: "failed", REMOVED: "removed" };
export const EMAIL_KINDS = { COPY: "copy", RESEND: "resend", SIGNOFF_NOTICE: "signoff_notice" };
export const EMAIL_STATUS = { SENT: "sent", FAILED: "failed" };
export const ACK_VIA = { APP: "app", EMAIL: "email" };
export const COURSE_LINK_SOURCE = { MANUAL: "manual", AI: "ai" };
export const SUGGESTION_DECISION = { SUGGESTED: "suggested", ACCEPTED: "accepted", REJECTED: "rejected" };

export const DEFAULT_PASS_THRESHOLD_PCT = 70;
export const REMEDIATION_DEFAULT_DUE_DAYS = 30;
export const SIGNOFF_TOKEN_DAYS = 30;
export const ACK_NAME_MIN_LENGTH = 3;
export const QUESTION_TEXT_MAX = 1000;
export const HELP_TEXT_MAX = 1000;
export const TEMPLATE_NAME_MAX = 200;
export const DASHBOARD_ANSWER_FETCH_CAP = 200;

/** Target roles a template can be aimed at (spec §6.1 TargetRoleId). */
export const TARGET_ROLES = [
  { roleId: 4, label: "Employee" },
  { roleId: 7, label: "Site Manager" },
  { roleId: 5, label: "Supervisor" },
  { roleId: 2, label: "District Administrator" },
];

export const EVAL_TABLES = {
  template: "EvalV2_Template",
  section: "EvalV2_Section",
  question: "EvalV2_Question",
  questionCourse: "EvalV2_QuestionCourse",
  templateVersion: "EvalV2_TemplateVersion",
  evaluation: "EvalV2_Evaluation",
  answer: "EvalV2_Answer",
  remediation: "EvalV2_Remediation",
  emailLog: "EvalV2_EmailLog",
  area: "EvalV2_Area",
  areaSite: "EvalV2_AreaSite",
  evaluatorGrant: "EvalV2_EvaluatorGrant",
};

export const EVAL_VIEWS = {
  userDirectory: "vw_EvalV2_UserDirectory",
  evaluations: "vw_EvalV2_Evaluations",
  remediationStatus: "vw_EvalV2_RemediationStatus",
  dashboardRows: "vw_EvalV2_DashboardRows",
};
